import { db } from '../../firebase';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  setDoc,
} from 'firebase/firestore';
import { OPTIMIZATION_STATUS } from './constants';
import { buildSolverPayload } from './buildSolverPayload';
import { SolverApi } from './SolverApi';

const runsCol = (planningId) => collection(db, 'plannings', planningId, 'optimization_runs');

export const OptimizationService = {
  async createOptimizationRun(planningId, { optimizationFile, createdBy }) {
    // Store a trimmed optimization file to avoid Firestore index explosion
    // Only keep fields needed by the UI and application of results.
    const idx = optimizationFile?.optimizationIndexes || {};
    const lightIndexes = {
      users: idx.users || {},
      events: idx.events || {},
      roles: idx.roles || {},
      staffRequired: idx.staffRequired || {},
      staffRequiredByRole: idx.staffRequiredByRole || {},
      eligibleRolesByUser: idx.eligibleRolesByUser || {},
      userUnavailable: idx.userUnavailable || {},
      eventInSameWeek: idx.eventInSameWeek || {},
      eventDateYmd: idx.eventDateYmd || {},
      // Keep small helpers; drop massive *DecisionVariables maps
      overlapsEdges: idx.overlapsEdges || [],
      linkedEdges: idx.linkedEdges || [],
      dayKeys: idx.dayKeys || [],
      employmentByUser: idx.employmentByUser || {},
      externalUsers: idx.externalUsers || [],
      currentAssigneesByRole: idx.currentAssigneesByRole || {},
      seriesKeyMap: idx.seriesKeyMap || {},
      eventSeriesKeyMap: idx.eventSeriesKeyMap || {},
      seriesRoleEvents: idx.seriesRoleEvents || {},
      adjacencyEdges: idx.adjacencyEdges || [],
      eventStartMs: idx.eventStartMs || {},
      eventEndMs: idx.eventEndMs || {},
      eventLocation: idx.eventLocation || {},
    };
    const lightOptimizationFile = {
      planningId: optimizationFile?.planningId,
      timeScope: optimizationFile?.timeScope,
      participants: optimizationFile?.participants || [],
      weeklyDefaults: optimizationFile?.weeklyDefaults || {},
      optimizationIndexes: lightIndexes,
      version: optimizationFile?.version,
      presetKey: optimizationFile?.presetKey,
      // keep config reference if small; avoid nesting very large objects here
      config: optimizationFile?.config ? { key: optimizationFile?.presetKey } : null,
    };

    // Store the light payload under a run document; mark status as QUEUED
    const payload = {
      optimization_file: lightOptimizationFile,
      status: OPTIMIZATION_STATUS.QUEUED,
      createdBy: createdBy || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Placeholders for backend references
      jobId: null, // filled when API queues the job
    };
    let runRef;
    try {
      runRef = await addDoc(runsCol(planningId), payload);
    } catch (e) {
      const msg = String(e?.message || '').toLowerCase();
      const code = String(e?.code || '').toLowerCase();
      // Rare edge: ALREADY_EXISTS from backend/emulator collision; retry with explicit new doc id
      if (msg.includes('already exists') || code.includes('already') || code.includes('exists')) {
        const retryRef = doc(runsCol(planningId)); // generates a fresh id
        await setDoc(retryRef, payload);
        runRef = retryRef;
      } else {
        throw e;
      }
    }
    const runId = runRef.id;

    // Build payload and submit to solver endpoint (non-blocking failure handling)
    try {
      // Respect preset/config if provided in optimization file
      const solverPayload = buildSolverPayload(optimizationFile, optimizationFile?.config || null);
      const resp = await SolverApi.submitJob(solverPayload);
      const jobId = resp.job_id || resp.id || null;
      if (jobId) {
        await updateDoc(doc(runsCol(planningId), runId), {
          jobId,
          // If submitted, we can consider RUNNING or keep QUEUED until poll says otherwise
          status: OPTIMIZATION_STATUS.RUNNING,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (e) {
      // If submission fails, mark run as FAILED
      await updateDoc(doc(runsCol(planningId), runId), {
        status: OPTIMIZATION_STATUS.FAILED,
        updatedAt: serverTimestamp(),
        error: String(e?.message || e || 'submit_failed'),
      });
    }

    return { id: runId };
  },

  async getLatestRun(planningId) {
    const q = query(runsCol(planningId), orderBy('createdAt', 'desc'), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  },

  async listRuns(planningId, { limitCount } = {}) {
    const parts = [orderBy('createdAt', 'desc')];
    if (limitCount && Number(limitCount) > 0) parts.push(limit(Number(limitCount)));
    const qy = query(runsCol(planningId), ...parts);
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async updateRunName(planningId, runId, name) {
    await updateDoc(doc(runsCol(planningId), runId), {
      name: name || null,
      updatedAt: serverTimestamp(),
    });
  },

  // Poll solver API for latest status and reflect into Firestore
  async fetchRunStatusFromAPI(planningId, run) {
    if (!run?.jobId) return run?.status || null;
    try {
      const remote = await SolverApi.getJob(run.jobId);
      const raw = remote.status || remote.state || null;
      let mapped = run.status || null;
      const s = String(raw || '').toUpperCase();
      if (s.includes('FAIL')) mapped = OPTIMIZATION_STATUS.FAILED;
      else if (s.includes('SUCC') || s.includes('DONE') || s.includes('COMPLETE')) mapped = OPTIMIZATION_STATUS.SUCCEEDED;
      else if (s.includes('RUN')) mapped = OPTIMIZATION_STATUS.RUNNING;
      else if (s.includes('QUEUE') || s.includes('PEND')) mapped = OPTIMIZATION_STATUS.QUEUED;
      if (mapped && mapped !== run.status) {
        await updateDoc(doc(runsCol(planningId), run.id), {
          status: mapped,
          updatedAt: serverTimestamp(),
        });
      }
      return mapped;
    } catch (e) {
      // On transient failures, keep prior status
      return run?.status || null;
    }
  },

  async updateRunStatus(planningId, runId, status) {
    await updateDoc(doc(db, 'plannings', planningId, 'optimization_runs', runId), {
      status,
      updatedAt: serverTimestamp(),
    });
  },

  // Fetch results from solver API (caller decides what to do with it)
  async fetchRunResult(_planningId, runIdOrRun) {
    const run = typeof runIdOrRun === 'object' ? runIdOrRun : null;
    const jobId = run?.jobId || null;
    if (!jobId) return null;
    const res = await SolverApi.getJobResult(jobId);
    return res;
  },

  // Clear assignments for specific events
  async clearAssignmentsForEvents(planningId, eventIds) {
    const batch = (await import('firebase/firestore')).writeBatch(db);
    for (const eid of eventIds) {
      const ref = doc(db, 'plannings', planningId, 'events', eid);
      batch.update(ref, { assignedTo: [], assignments: {} });
    }
    await batch.commit();
  },

  // Batch set assignments per event
  async setAssignmentsBatch(planningId, byEvent) {
    const batch = (await import('firebase/firestore')).writeBatch(db);
    for (const [eid, value] of Object.entries(byEvent || {})) {
      const ref = doc(db, 'plannings', planningId, 'events', eid);
      if (Array.isArray(value)) {
        // Legacy shape: eventId -> [userIds]
        batch.update(ref, { assignedTo: value, assignments: {} });
      } else if (value && typeof value === 'object') {
        // New shape: eventId -> { roleId: [userIds] }
        const assignments = {};
        const all = new Set();
        for (const [roleId, uids] of Object.entries(value)) {
          const arr = Array.isArray(uids) ? uids.filter(Boolean) : [];
          assignments[roleId] = arr;
          arr.forEach((u) => all.add(u));
        }
        batch.update(ref, { assignments, assignedTo: Array.from(all) });
      } else {
        batch.update(ref, { assignedTo: [], assignments: {} });
      }
    }
    await batch.commit();
  },

  // Apply assignments from solver result using the run's optimization_file mappings
  async applyAssignmentsFromResult(planningId, run, result) {
    if (!planningId || !run || !result) return false;
    const vars = result.vars || result.Vars || result.variables || {};
    const idx = run.optimization_file?.optimizationIndexes || {};
    const usersMap = idx.users || {}; // U# -> userId
    const eventsMap = idx.events || {}; // E# -> eventId
    const rolesMap = idx.roles || {}; // R# -> roleId/name
    const inScopeEventIds = Object.values(eventsMap);
    if (!inScopeEventIds.length) return false;

    // 1) Clear assignments for all in-scope events
    await this.clearAssignmentsForEvents(planningId, inScopeEventIds);

    // 2) Build assignments map from decision variables set to 1
    const byEvent = {};
    for (const [name, value] of Object.entries(vars)) {
      // role-aware variable name: x_U#_E#_R#
      const m = /^x_(U\d+)_(E\d+)_(R\d+)$/.exec(name);
      if (!m) continue;
      const uKey = m[1];
      const eKey = m[2];
      const rKey = m[3];
      const on = Number(value) >= 1 || value === true;
      if (!on) continue;
      const uid = usersMap[uKey];
      const eid = eventsMap[eKey];
      const roleId = rolesMap[rKey];
      if (!uid || !eid || !roleId) continue;
      if (!byEvent[eid]) byEvent[eid] = {};
      if (!byEvent[eid][roleId]) byEvent[eid][roleId] = [];
      if (!byEvent[eid][roleId].includes(uid)) byEvent[eid][roleId].push(uid);
    }

    // 3) Persist assignments in batch
    await this.setAssignmentsBatch(planningId, byEvent);
    return true;
  },
};
